import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Typography, Box, Chip, TextField } from '@mui/material';

import Drawer from '../common/Drawer';
import Input from '../common/form/Input';
import AutoComplete from '../common/form/AutoComplete';
import SaveCancelButtons from '../common/buttons/SaveCancelButtons';
import { AppDispatch, RootState } from '../../data/store';
import { createUserGroup } from '../../data/thunks/userGroupThunk';
import {
	CreateUserGroupInput,
	GroupMemberOption,
} from '../../data/models/userGroup';
import { fetchUsers } from '../../data/thunks/userThunk';
import { fetchCompanyExternalUsers } from '../../data/thunks/companyExternalThunk';
import { closeModal } from '../../data/stores/modalStore';

type FormValues = {
	name: string;
	// description: string;
	selectedMembers: GroupMemberOption[];
};

export default function AddGroupModal() {
	const { openId } = useSelector((state: RootState) => state.modalState);
	const open = openId === 'groupCreationModal';
	const dispatch = useDispatch<AppDispatch>();

	const { company } = useSelector((state: RootState) => state.companyState);
	const { users } = useSelector((state: RootState) => state.companyState);
	const { companyExternalUsers } = useSelector(
		(state: RootState) => state.companyExternalUserState
	);

	const [memberOptions, setMemberOptions] = useState<GroupMemberOption[]>([]);

	const defaultValues: FormValues = {
		name: '',
		selectedMembers: [],
	};

	const { handleSubmit, control, reset, formState } = useForm({
		defaultValues,
		mode: 'onBlur',
	});
	const { errors } = formState;

	// Load users and external users when modal opens
	useEffect(() => {
		if (open && company?.id) {
			// Load users
			if (!users?.initialized) {
				dispatch(
					fetchUsers({
						// companyId: company.id,
						take: 1000, // Get all users for selection
						page: 0,
					})
				);
			}

			// Load external users
			if (!companyExternalUsers?.initialized) {
				dispatch(
					fetchCompanyExternalUsers({
						// companyId: company.id,
						take: 1000, // Get all external users for selection
						page: 0,
					})
				);
			}
		}
	}, [
		open,
		company?.id,
		dispatch,
		users?.initialized,
		companyExternalUsers?.initialized,
	]);

	// Create member options for autocomplete
	useEffect(() => {
		const options: GroupMemberOption[] = [];

		// Add internal users
		if (users?.list) {
			users.list.forEach((user) => {
				options.push({
					id: user.id,
					firstName: user.firstName,
					lastName: user.lastName,
					email: user.email,
					role: user.role,
					type: 'user',
					display: `${user.firstName} ${user.lastName} (${user.email})`,
				});
			});
		}

		// Add external users
		if (companyExternalUsers?.list) {
			companyExternalUsers.list.forEach((user) => {
				options.push({
					id: user.id,
					firstName: user.firstName,
					lastName: user.lastName,
					email: user.email,
					role: user.role,
					type: 'externalUser',
					display: `${user.firstName} ${user.lastName} (${user.email}) - External`,
				});
			});
		}

		setMemberOptions(options);
	}, [users?.list, companyExternalUsers?.list]);

	const resetForm = () => {
		reset(defaultValues);
	};

	const handleOnSaveClick = (data: FormValues) => {
		// Separate users and external users
		const userIds: string[] = [];
		const externalUserIds: string[] = [];

		data.selectedMembers.forEach((member) => {
			if (member.type === 'user') {
				userIds.push(member.id);
			} else {
				externalUserIds.push(member.id);
			}
		});

		const createData: CreateUserGroupInput = {
			name: data.name,
			// description: data.description || undefined,
			users: userIds.length > 0 ? userIds : undefined,
			externalUsers:
				externalUserIds.length > 0 ? externalUserIds : undefined,
		};

		dispatch(createUserGroup(createData));
		resetForm();
		dispatch(closeModal());
	};

	const handleCancelCloseOnClick = () => {
		resetForm();
		dispatch(closeModal());
	};

	if (!open) return <></>;

	return (
		<Drawer
			open={open}
			handleClose={handleCancelCloseOnClick}
			title="Add Group"
			width={600}
		>
			<Container>
				<Grid container spacing={3}>
					{/* Group Name */}
					<Grid item xs={12}>
						<Controller
							name="name"
							control={control}
							rules={{ required: 'Group name is required' }}
							render={({ field }) => (
								<Input
									{...field}
									label="Group name"
									required
									fullWidth
									error={!!errors.name}
									helperText={errors.name?.message}
								/>
							)}
						/>
					</Grid>

					{/* People Selection */}
					<Grid item xs={12}>
						<Controller
							name="selectedMembers"
							control={control}
							render={({ field }) => (
								<AutoComplete
									{...field}
									label="People"
									multiple={true}
									options={memberOptions}
									placeholder="Select people to add to group..."
									getOptionLabel={(option) => option.display}
									isOptionEqualToValue={(option, value) =>
										option.id === value.id
									}
									renderTags={(value, getTagProps) =>
										value.map((option, index) => (
											<Chip
												{...getTagProps({ index })}
												key={option.id}
												label={`${option.firstName} ${option.lastName}`}
												size="small"
												color={
													option.type === 'user'
														? 'primary'
														: 'secondary'
												}
											/>
										))
									}
									renderOption={(props, option) => (
										<li {...props}>
											<Box
												sx={{
													display: 'flex',
													flexDirection: 'column',
													width: '100%',
												}}
											>
												<Typography variant="body2">
													{option.firstName}{' '}
													{option.lastName}
												</Typography>
												<Typography
													variant="caption"
													color="text.secondary"
												>
													{option.email} •{' '}
													{option.type === 'user'
														? 'Internal'
														: 'External'}{' '}
													{option.role &&
														`• ${option.role}`}
												</Typography>
											</Box>
										</li>
									)}
									onChange={(_, value) =>
										field.onChange(value)
									}
								/>
							)}
						/>
					</Grid>
				</Grid>

				<ButtonContainer>
					<SaveCancelButtons
						handleSave={handleSubmit(handleOnSaveClick)}
						handleCancel={handleCancelCloseOnClick}
					/>
				</ButtonContainer>
			</Container>
		</Drawer>
	);
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	padding: 16px 0;
`;

const ButtonContainer = styled.div`
	position: absolute;
	bottom: 16px;
	right: 16px;
`;
